export function formatCurrency(
  amount: number,
  currency: string = 'AUD',
): string {
  return new Intl.NumberFormat(navigator.language, {
    currency,
    currencyDisplay: 'narrowSymbol',
    style: 'currency',
  }).format(amount);
}

/**
 * ISO 4217 codes that should render a bare "$". Many "dollar"/"peso" currencies
 * share the $ glyph, and `Intl` narrowSymbol resolves them to "A$" / "NZ$"
 * / "CA$" / "US$" under an `en` locale.
 * Pinning them here keeps them to a plain $.
 */
const DOLLAR_CURRENCY_CODES = new Set([
  'AUD',
  'USD',
  'NZD',
  'CAD',
  'NAD',
  'ARS',
  'SGD',
  'HKD',
  'MXN',
  'CLP',
  'COP',
  'UYU',
  'TWD',
  'BND',
  'FJD',
  'BSD',
  'BBD',
  'BMD',
  'BZD',
  'XCD',
]);

/**
 * Explicit symbol overrides for non-dollar currencies we want to pin rather
 * than leave to `Intl` (either to guarantee the glyph or because `Intl` is
 * unreliable for them). Any other ISO 4217 code falls through to `Intl`
 * narrowSymbol, so new currencies are handled automatically.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  GBP: '£',
  ZAR: 'R',
  HUF: 'Ft',
};

/**
 * Country name (ISO 3166-1, as stored on a society) → ISO 4217 currency code.
 * Defensive fallback only, for the rare society with a missing currency code.
 */
const COUNTRY_CURRENCY_CODES: Record<string, string> = {
  Argentina: 'ARS',
  Australia: 'AUD',
  Canada: 'CAD',
  Hungary: 'HUF',
  Namibia: 'NAD',
  'New Zealand': 'NZD',
  Portugal: 'EUR',
  'South Africa': 'ZAR',
  'United Kingdom': 'GBP',
  'United States': 'USD',
  Zimbabwe: 'USD',
};

/**
 * Best-effort symbol for an ISO 4217 code via `Intl`, using a fixed `en` locale
 * so the result doesn't depend on the viewer's browser locale. Returns null
 * when the code is unknown or `Intl` has no symbol (it echoes the code back).
 */
function symbolFromIntl(code: string): string | null {
  try {
    const parts = new Intl.NumberFormat('en', {
      currency: code,
      currencyDisplay: 'narrowSymbol',
      style: 'currency',
    }).formatToParts(1);
    const value = parts.find((part) => part.type === 'currency')?.value;
    if (!value || value.toUpperCase() === code) return null;
    return value;
  } catch {
    return null;
  }
}

/** Resolves a symbol for a normalised ISO 4217 code, or null if unresolved. */
function resolveSymbolForCode(code: string): string | null {
  if (DOLLAR_CURRENCY_CODES.has(code)) return '$';
  return CURRENCY_SYMBOLS[code] ?? symbolFromIntl(code);
}

/** Default currency (Australian dollar) used when a society has no currency. */
export const DEFAULT_CURRENCY_CODE = 'AUD';

/** Fallback currency symbol (Australian dollar `$`) when none can be resolved. */
export const DEFAULT_CURRENCY_SYMBOL =
  resolveSymbolForCode(DEFAULT_CURRENCY_CODE) ?? '$';

/**
 * Resolves the currency symbol to display for a society from its ISO 4217
 * `currency` code (e.g. `ZAR` → `R`, `GBP` → `£`, `AUD` → `$`). Unknown-but-real
 * codes resolve via `Intl`, so newly added currencies work without a code
 * change. Falls back to deriving the code from the society's country, then to
 * the Australian-dollar default.
 */
export function getCurrencySymbol(
  currency?: string | null,
  country?: string | null,
): string {
  const code = currency?.trim().toUpperCase();
  const fromCode = code ? resolveSymbolForCode(code) : null;
  if (fromCode) return fromCode;

  const countryCode = country && COUNTRY_CURRENCY_CODES[country.trim()];
  const fromCountry = countryCode ? resolveSymbolForCode(countryCode) : null;
  if (fromCountry) return fromCountry;

  return DEFAULT_CURRENCY_SYMBOL;
}
